<template>
    <div class="animated fadeIn">
        <ComponentHeader
            class="mt-3"
            :title="lead && lead.name ? lead.name : 'Lead Details'"
            :hasSearch="false"
            :hasCreate="false"
            :hasReload="true"
            :hasback="true"
            :isLoading="isLoading"
            @back-press="$router.push({ path: '/channels/leads' })"
            @reload-data="refreshLead"
        >
            <template #heading-before-search>
                <div class="lead-header-right">
                    <div class="owner-card">
                        <div class="owner-avatar">
                            {{ getInitials(lead.owner) }}
                        </div>
                        <div class="owner-meta">
                            <div class="owner-name">{{ lead.owner }}</div>
                            <div class="owner-role">Owner</div>
                        </div>
                    </div>

                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="change-owner-btn"
                        @click="openChangeOwnerModal"
                    >
                        <i class="fa fa-pencil mr-1"></i>
                        Change Owner
                    </b-button>
                </div>
            </template>
        </ComponentHeader>

        <div
            v-if="(lead && lead.company) || (lead && lead.jobTitle)"
            class="lead-subtitle-line"
        >
            <span v-if="lead.company">{{ lead.company }}</span>
            <span v-if="lead.company && lead.jobTitle"> • </span>
            <span v-if="lead.jobTitle">{{ lead.jobTitle }}</span>
        </div>

        <b-row class="mt-3">
            <b-col cols="12">
                <div class="status-strip">
                    <div class="status-left">
                        <i class="fa fa-check-circle status-icon"></i>
                        <span class="status-text">
                            <strong>Active follow-up in progress</strong> by
                            {{ lead.owner }}. Others can view but cannot
                            contact.
                        </span>
                    </div>
                    <span class="status-pill">
                        {{ lead.status }}
                    </span>
                </div>
            </b-col>
        </b-row>

        <b-row class="mt-4">
            <b-col cols="12" md="4" lg="3" class="mb-4">
                <!-- Quick Actions Card -->
                <b-card class="mb-3" header="Quick Actions">
                    <div class="quick-actions">
                        <b-button
                            variant="outline-primary"
                            block
                            class="mb-2"
                            :href="`tel:${lead.phone}`"
                        >
                            <i class="fa fa-phone mr-2"></i>
                            Make a Call
                        </b-button>

                        <b-button
                            variant="outline-primary"
                            block
                            class="mb-2"
                            @click="sendEmail"
                        >
                            <!-- :href="`mailto:${lead.email}`" -->
                            <i class="fa fa-envelope mr-2"></i>
                            Send Follow-up Email
                        </b-button>

                        <b-button
                            variant="outline-primary"
                            block
                            class="mb-2"
                            @click="addNote"
                        >
                            <i class="fa fa-sticky-note mr-2"></i>Add Follow-up
                            Note
                        </b-button>
                        <b-button
                            variant="outline-primary"
                            block
                            @click="scheduleMeeting"
                        >
                            <i class="fa fa-calendar mr-2"></i>Schedule Meeting
                        </b-button>
                    </div>
                </b-card>

                <!-- Contact Information Card -->
                <b-card class="mb-2" header="Contact Information">
                    <div class="contact-info">
                        <div class="info-item mb-2">
                            <div class="info-label">
                                <i class="fa fa-envelope mr-2 text-muted"></i
                                >Email
                            </div>
                            <div class="info-value">{{ lead.email }}</div>
                        </div>
                        <div class="info-item mb-2">
                            <div class="info-label">
                                <i class="fa fa-phone mr-2 text-muted"></i>Phone
                            </div>
                            <div class="info-value">{{ lead.phone }}</div>
                        </div>
                        <div class="info-item mb-2">
                            <div class="info-label">
                                <i class="fa fa-building mr-2 text-muted"></i
                                >Company & Title
                            </div>
                            <div class="info-value">{{ lead.company }}</div>
                            <div class="info-value text-muted">
                                {{ lead.jobTitle }}
                            </div>
                        </div>
                        <!--<div class="info-item">
                            <div class="info-label">
                                <i class="fa fa-map-marker mr-2 text-muted"></i
                                >Location
                            </div>
                            <div class="info-value">San Francisco</div>
                        </div>-->
                    </div>
                </b-card>

                <!-- Lead Details Card -->
                <b-card class="mb-2" header="Lead Details">
                    <div class="lead-details">
                        <div class="detail-item mb-2">
                            <div class="detail-label">Source</div>
                            <div class="detail-value">
                                {{ capitalizeFirst(lead.source) }}
                            </div>
                        </div>
                        <div class="detail-item mb-2">
                            <div class="detail-label">Status</div>
                            <div>
                                <b-badge
                                    :variant="getStatusVariant(lead.status)"
                                >
                                    {{ capitalizeFirst(lead.status) }}
                                </b-badge>
                            </div>
                        </div>
                        <div class="detail-item">
                            <div class="detail-label">Created Date</div>
                            <div class="detail-value">
                                {{ formatDate(lead.created) }}
                            </div>
                        </div>
                    </div>
                </b-card>

                <!-- Team Access Card -->
                <b-card header="Team Access">
                    <div class="team-access">
                        <div class="team-member">
                            <div class="team-avatar">
                                {{ getInitials(lead.owner) }}
                            </div>

                            <div class="team-info">
                                <div class="team-name">{{ lead.owner }}</div>
                                <div class="team-access-level">Full Access</div>
                            </div>

                            <b-badge variant="primary" class="team-role-badge">
                                Owner
                            </b-badge>
                        </div>

                        <div class="team-member">
                            <div class="team-avatar">SW</div>

                            <div class="team-info">
                                <div class="team-name">Sarah Williams</div>
                                <div class="team-access-level">Can View</div>
                            </div>

                            <b-badge variant="info" class="team-role-badge">
                                Collaborator
                            </b-badge>
                        </div>

                        <div class="team-member">
                            <div class="team-avatar">MC</div>

                            <div class="team-info">
                                <div class="team-name">Mike Chen</div>
                                <div class="team-access-level">Read Only</div>
                            </div>

                            <b-badge
                                variant="secondary"
                                class="team-role-badge"
                            >
                                Viewer
                            </b-badge>
                        </div>
                    </div>
                </b-card>
            </b-col>

            <b-col cols="12" md="8" lg="9">
                <b-card>
                    <template #header>
                        <div
                            class="d-flex justify-content-between align-items-center"
                        >
                            <h5 class="mb-0">Follow-Up Timeline</h5>
                            <div class="d-flex justify-content-end">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    class="mr-2"
                                    @click="addNote"
                                >
                                    <i class="fa fa-sticky-note mr-1"></i>Add
                                    Note
                                </b-button>
                                <b-button
                                    variant="primary"
                                    size="sm"
                                    @click="sendEmail"
                                >
                                    <i class="fa fa-envelope mr-1"></i>Send
                                    Email
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="timeline-wrapper" v-if="!isLoading">
                        <!-- Timeline Item - Initial Submission -->
                        <div
                            v-if="lead.subject || lead.message"
                            class="timeline-card contact-form-card"
                        >
                            <div class="timeline-line"></div>
                            <div class="timeline-icon-wrapper">
                                <div class="timeline-icon contact-form-icon">
                                    <i class="fa fa-file-text"></i>
                                </div>
                            </div>
                            <div class="timeline-card-content">
                                <div class="timeline-header">
                                    <div class="d-flex align-items-center">
                                        <span
                                            class="timeline-type contact-form-type"
                                        >
                                            {{
                                                formatSource(
                                                    lead.originalSource ||
                                                        lead.source,
                                                )
                                            }}</span
                                        >
                                    </div>
                                    <span class="timeline-meta-text">
                                        Initial submission
                                        <span class="mx-2">•</span>
                                        {{
                                            lead.createdAt
                                                ? formatDateTimeFromISO(
                                                      lead.createdAt,
                                                  )
                                                : formatDate(lead.created)
                                        }}
                                    </span>
                                </div>
                                <div
                                    v-if="lead.subject"
                                    class="contact-form-subject"
                                >
                                    <strong>Subject:</strong> {{ lead.subject }}
                                </div>
                                <div
                                    v-if="lead.message"
                                    class="timeline-body contact-form-body"
                                >
                                    {{ lead.message }}
                                </div>
                            </div>
                        </div>

                        <!-- Timeline Items - Separated Emails and Notes -->
                        <div
                            v-for="(item, index) in timelineItems"
                            :key="item._id || `item-${index}`"
                            class="timeline-card"
                            :class="{
                                'note-card': item.isNote,
                                'email-card': item.isEmail,
                            }"
                        >
                            <div class="timeline-line"></div>
                            <div class="timeline-icon-wrapper">
                                <div
                                    class="timeline-icon"
                                    :class="{
                                        'note-icon': item.isNote,
                                        'email-icon': item.isEmail,
                                    }"
                                >
                                    <i
                                        :class="{
                                            'fa fa-sticky-note': item.isNote,
                                            'fa fa-envelope': item.isEmail,
                                        }"
                                    ></i>
                                </div>
                            </div>
                            <div class="timeline-card-content">
                                <!-- Email Entry -->
                                <template v-if="item.isEmail">
                                    <div class="timeline-header">
                                        <div class="d-flex align-items-center">
                                            <span
                                                class="timeline-type email-type"
                                            >
                                                EMAIL
                                            </span>
                                            <b-badge
                                                v-if="item.status"
                                                :variant="
                                                    item.status === 'SENT'
                                                        ? 'danger'
                                                        : 'success'
                                                "
                                                class="ml-2"
                                                :class="{
                                                    'email-status-badge':
                                                        item.status === 'SENT',
                                                    'email-replied-badge':
                                                        item.status !== 'SENT',
                                                }"
                                            >
                                                <i
                                                    :class="
                                                        item.status === 'SENT'
                                                            ? 'fa fa-eye mr-1'
                                                            : 'fa fa-check mr-1'
                                                    "
                                                ></i>
                                                {{
                                                    item.status === "SENT"
                                                        ? "opened"
                                                        : "replied"
                                                }}
                                            </b-badge>
                                        </div>
                                        <span class="timeline-meta-text">
                                            <span
                                                v-if="
                                                    item.direction ===
                                                    'OUTBOUND'
                                                "
                                            >
                                                Sent by {{ userName }}
                                            </span>
                                            <span v-else>
                                                Received from {{ lead.name }}
                                            </span>
                                            <span class="mx-2">•</span>
                                            {{
                                                item.sentAt
                                                    ? formatDateTimeFromISO(
                                                          item.sentAt,
                                                      )
                                                    : item.createdAt
                                                      ? formatDateTimeFromISO(
                                                            item.createdAt,
                                                        )
                                                      : ""
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="item.subject"
                                        class="email-subject-title"
                                    >
                                        {{ item.subject }}
                                    </div>
                                    <div
                                        v-if="item.body"
                                        class="email-content-preview"
                                    >
                                        <span
                                            v-html="getEmailPreview(item.body)"
                                        ></span>
                                    </div>
                                    <div
                                        v-if="item.isEmail"
                                        class="timeline-link"
                                    >
                                        <i class="fa fa-eye mr-1"></i>
                                        <a
                                            href="#"
                                            class="text-primary"
                                            @click.prevent="
                                                viewFullEmail({
                                                    subject: item.subject || '',
                                                    message: item.message || '',
                                                    body: item.body || '',
                                                    sentDateTime:
                                                        item.sentAt ||
                                                        item.createdAt ||
                                                        '',
                                                    status:
                                                        item.status === 'SENT'
                                                            ? 'sent'
                                                            : 'replied',
                                                    statusLabel:
                                                        item.status === 'SENT'
                                                            ? 'Sent'
                                                            : 'Replied',
                                                })
                                            "
                                            >View Full Email</a
                                        >
                                    </div>
                                </template>

                                <!-- Note Entry -->
                                <template v-else-if="item.isNote">
                                    <div class="timeline-header">
                                        <div class="d-flex align-items-center">
                                            <span
                                                class="timeline-type note-type"
                                            >
                                                NOTE
                                            </span>
                                        </div>
                                        <span class="timeline-meta-text">
                                            {{ userName }} added a note
                                            <span class="mx-2">•</span>
                                            {{
                                                item.createdAt
                                                    ? formatDateTimeFromISO(
                                                          item.createdAt,
                                                      )
                                                    : ""
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="item.message"
                                        class="note-content-box"
                                    >
                                        {{ item.message }}
                                    </div>
                                    <div
                                        v-if="
                                            item.nextAction ||
                                            item.nextActionDate
                                        "
                                        class="note-next-action-box"
                                        :class="{
                                            'next-action-expired':
                                                getNextActionDateStatus(
                                                    item.nextActionDate,
                                                )?.status === 'expired',
                                            'next-action-urgent':
                                                getNextActionDateStatus(
                                                    item.nextActionDate,
                                                )?.status === 'today' ||
                                                getNextActionDateStatus(
                                                    item.nextActionDate,
                                                )?.status === 'tomorrow',
                                            'next-action-soon':
                                                getNextActionDateStatus(
                                                    item.nextActionDate,
                                                )?.status === 'soon',
                                        }"
                                    >
                                        <div class="next-action-header">
                                            <i class="fa fa-calendar mr-2"></i>
                                            <strong>Next Action:</strong>
                                            <b-badge
                                                v-if="
                                                    getNextActionDateStatus(
                                                        item.nextActionDate,
                                                    )
                                                "
                                                :variant="
                                                    getNextActionDateStatus(
                                                        item.nextActionDate,
                                                    )?.status === 'expired'
                                                        ? 'danger'
                                                        : getNextActionDateStatus(
                                                                item.nextActionDate,
                                                            )?.status ===
                                                                'today' ||
                                                            getNextActionDateStatus(
                                                                item.nextActionDate,
                                                            )?.status ===
                                                                'tomorrow'
                                                          ? 'warning'
                                                          : 'info'
                                                "
                                                class="ml-2 next-action-status-badge"
                                            >
                                                <i
                                                    :class="
                                                        getNextActionDateStatus(
                                                            item.nextActionDate,
                                                        )?.status === 'expired'
                                                            ? 'fa fa-exclamation-circle mr-1'
                                                            : getNextActionDateStatus(
                                                                    item.nextActionDate,
                                                                )?.status ===
                                                                    'today' ||
                                                                getNextActionDateStatus(
                                                                    item.nextActionDate,
                                                                )?.status ===
                                                                    'tomorrow'
                                                              ? 'fa fa-clock mr-1'
                                                              : 'fa fa-info-circle mr-1'
                                                    "
                                                ></i>
                                                {{
                                                    getNextActionDateStatus(
                                                        item.nextActionDate,
                                                    )?.label
                                                }}
                                            </b-badge>
                                        </div>
                                        <div
                                            v-if="item.nextAction"
                                            class="next-action-text"
                                        >
                                            {{ item.nextAction }}
                                        </div>
                                        <div
                                            v-if="item.nextActionDate"
                                            class="next-action-due"
                                            :class="{
                                                'text-danger':
                                                    getNextActionDateStatus(
                                                        item.nextActionDate,
                                                    )?.status === 'expired',
                                                'text-warning':
                                                    getNextActionDateStatus(
                                                        item.nextActionDate,
                                                    )?.status === 'today' ||
                                                    getNextActionDateStatus(
                                                        item.nextActionDate,
                                                    )?.status === 'tomorrow',
                                                'text-info':
                                                    getNextActionDateStatus(
                                                        item.nextActionDate,
                                                    )?.status === 'soon',
                                            }"
                                        >
                                            Due:
                                            {{
                                                formatDateOnlyFromISO(
                                                    item.nextActionDate,
                                                )
                                            }}
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div
                            v-if="
                                !lead.subject &&
                                !lead.message &&
                                (!timelineItems || timelineItems.length === 0)
                            "
                            class="text-center py-4 text-muted"
                        >
                            No timeline items available.
                        </div>
                    </div>
                    <div v-if="isLoading" class="text-center py-4">
                        <atom-spinner
                            :animation-duration="1000"
                            :size="60"
                            color="#ff1d5e"
                        />
                        <p class="mt-2">Loading lead data...</p>
                    </div>
                </b-card>

                <!-- Summary cards -->
                <b-card class="mt-4 summary-cards-card p-3">
                    <b-row class="summary-row text-center align-items-center">
                        <b-col cols="12" md="4" class="mb-2 mb-md-0">
                            <div class="summary-card bg-email">
                                <div class="summary-icon">
                                    <i class="fa fa-envelope"></i>
                                </div>
                                <div class="summary-count">
                                    {{ leadStats.emailsSent }}
                                </div>
                                <div class="summary-label">Emails Sent</div>
                            </div>
                        </b-col>
                        <b-col cols="12" md="4" class="mb-2 mb-md-0">
                            <div class="summary-card bg-notes">
                                <div class="summary-icon">
                                    <i class="fa fa-sticky-note"></i>
                                </div>
                                <div class="summary-count">
                                    {{ leadStats.notesAdded }}
                                </div>
                                <div class="summary-label">Notes Added</div>
                            </div>
                        </b-col>
                        <b-col cols="12" md="4">
                            <div class="summary-card bg-replies">
                                <div class="summary-icon">
                                    <i class="fa fa-check-circle"></i>
                                </div>
                                <div class="summary-count">
                                    {{ leadStats.repliesReceived }}
                                </div>
                                <div class="summary-label">
                                    Replies Received
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>

        <!-- Change Owner Modal -->
        <b-modal
            id="changeOwnerModal"
            ref="changeOwnerModal"
            size="lg"
            centered
            hide-footer
            title="Change Lead Owner"
            modal-class="center-title-modal leads-modal"
            @hidden="resetChangeOwnerForm"
        >
            <p class="modal-subtitle">
                Reassign this lead to another team member. Both parties will be
                notified.
            </p>

            <div class="add-note-body">
                <b-form-group label="Current Owner" label-class="add-note-label">
                    <b-form-input
                        :value="lead.owner"
                        readonly
                        disabled
                        class="add-note-input"
                    />
                </b-form-group>

                <b-form-group label="New Owner *" label-class="add-note-label">
                    <b-form-select
                        v-model="changeOwnerForm.newOwner"
                        :options="ownerOptions"
                        class="add-note-input"
                        placeholder="Select new owner"
                    />
                </b-form-group>

                <b-form-group
                    label="Reason for Change *"
                    label-class="add-note-label"
                >
                    <b-form-select
                        v-model="changeOwnerForm.reason"
                        :options="reasonOptions"
                        class="add-note-input"
                        placeholder="Select reason"
                    />
                </b-form-group>

                <b-form-group
                    label="Additional Note (Optional)"
                    label-class="add-note-label"
                >
                    <b-form-textarea
                        v-model="changeOwnerForm.note"
                        placeholder="Add any context for this change..."
                        rows="4"
                        class="add-note-textarea"
                    />
                </b-form-group>
            </div>

            <div class="modal-actions">
                <b-button
                    variant="outline-primary"
                    @click="$refs.changeOwnerModal.hide()"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    @click="changeOwnerAction"
                    :disabled="!changeOwnerForm.newOwner || !changeOwnerForm.reason"
                >
                    Change Owner
                </b-button>
            </div>
        </b-modal>

        <!-- Add Follow-up Note Modal -->
        <b-modal
            id="addNoteModal"
            ref="addNoteModal"
            size="lg"
            centered
            hide-footer
            title="Add Follow-up Note"
            modal-class="center-title-modal leads-modal"
            @hidden="resetNoteForm"
        >
            <p class="modal-subtitle">
                Record your conversation and plan next actions for
                <strong>{{ lead.name }}</strong>
            </p>

            <div
                class="add-note-body"
                :class="{ 'loading-overlay-container': isSavingNote }"
            >
                <div v-if="isSavingNote" class="loading-overlay">
                    <atom-spinner
                        :animation-duration="1000"
                        :size="60"
                        color="#ff1d5e"
                    />
                </div>
                <b-form-group label="Follow-up Note *" label-class="add-note-label">
                    <b-form-textarea
                        v-model="noteForm.note"
                        placeholder="What did you discuss? What are the key takeaways?"
                        rows="6"
                        class="add-note-textarea"
                        :disabled="isSavingNote"
                    />
                </b-form-group>

                <b-form-group
                    label="Next Action (Optional)"
                    label-class="add-note-label"
                >
                    <b-form-input
                        v-model="noteForm.nextAction"
                        placeholder="e.g., Send pricing proposal, Schedule demo call"
                        class="add-note-input"
                        :disabled="isSavingNote"
                    />
                </b-form-group>

                <b-form-group
                    label="Next Action Date (Optional)"
                    label-class="add-note-label"
                >
                    <b-form-input
                        v-model="noteForm.nextActionDate"
                        type="date"
                        class="add-note-input add-note-date-input"
                        :disabled="isSavingNote"
                    />
                </b-form-group>
            </div>

            <div class="modal-actions">
                <b-button
                    variant="outline-primary"
                    @click="$refs.addNoteModal.hide()"
                    :disabled="isSavingNote"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    @click="saveNote"
                    :disabled="!noteForm.note || isSavingNote"
                >
                    <atom-spinner
                        v-if="isSavingNote"
                        :animation-duration="1000"
                        :size="20"
                        color="#ffffff"
                        class="mr-2"
                    />
                    <i v-else class="fa fa-sticky-note mr-1"></i>
                    {{ isSavingNote ? "Saving..." : "Save Note" }}
                </b-button>
            </div>
        </b-modal>

        <!-- View Full Email Modal -->
        <b-modal
            id="viewEmailModal"
            ref="viewEmailModal"
            size="lg"
            centered
            hide-footer
            title="View Follow-up Email"
            modal-class="center-title-modal leads-modal"
        >
            <p class="modal-subtitle">
                Complete details of the follow-up email sent to
                <strong>{{ lead.name }}</strong>
            </p>

                <!-- Email Details Section -->
                <div class="view-email-details">
                    <div class="email-details-left">
                        <div class="email-detail-item">
                            <div class="email-detail-label">Sent By</div>
                            <div class="email-detail-value">
                                <span class="email-avatar">
                                    {{ getInitials(lead.owner) }}
                                </span>
                                <span class="email-owner-name">
                                    {{ lead.owner }}
                                </span>
                            </div>
                        </div>
                        <div class="email-detail-item">
                            <div class="email-detail-label">Recipient</div>
                            <div class="email-detail-value">
                                {{ lead.name }}
                                <span class="text-muted"
                                    >({{ lead.email }})</span
                                >
                            </div>
                        </div>
                    </div>
                    <div class="email-details-right">
                        <div class="email-detail-item">
                            <div class="email-detail-label">
                                Sent Date & Time
                            </div>
                            <div class="email-detail-value">
                                {{
                                    selectedEmail && selectedEmail.sentDateTime
                                        ? formatDateTimeFromISO(
                                              selectedEmail.sentDateTime,
                                          )
                                        : ""
                                }}
                            </div>
                        </div>
                        <div class="email-detail-item">
                            <div class="email-detail-label">Status</div>
                            <div>
                                <b-badge
                                    :variant="
                                        selectedEmail &&
                                        selectedEmail.status === 'replied'
                                            ? 'success'
                                            : 'danger'
                                    "
                                    class="email-status-replied-badge"
                                >
                                    <i
                                        :class="
                                            selectedEmail &&
                                            selectedEmail.status === 'replied'
                                                ? 'fa fa-check mr-1'
                                                : 'fa fa-eye mr-1'
                                        "
                                    ></i>
                                    {{
                                        selectedEmail
                                            ? selectedEmail.statusLabel
                                            : ""
                                    }}
                                </b-badge>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Body -->
                <div class="add-note-body">
                    <b-form-group label="Subject" label-class="add-note-label">
                        <b-form-input
                            :value="selectedEmail ? selectedEmail.subject : ''"
                            readonly
                            class="add-note-input"
                            style="
                                background-color: #f8f9fa;
                                cursor: not-allowed;
                            "
                        />
                    </b-form-group>

                    <b-form-group label="Message" label-class="add-note-label">
                        <div
                            v-if="
                                selectedEmail &&
                                (selectedEmail.body || selectedEmail.message)
                            "
                            class="email-message-html"
                            v-html="
                                formatEmailBody(
                                    selectedEmail.body || selectedEmail.message,
                                )
                            "
                        ></div>
                        <div v-else class="text-muted">No message content</div>
                    </b-form-group>

                    <!-- Engagement Alert -->
                    <div
                        v-if="
                            selectedEmail && selectedEmail.status === 'replied'
                        "
                        class="email-engagement-alert"
                    >
                        <i class="fa fa-check-circle"></i>
                        <div>
                            <strong
                                >The recipient has replied to this email</strong
                            >
                            <p>
                                This indicates positive engagement. Check your
                                inbox for their response.
                            </p>
                        </div>
                    </div>
                    <div
                        v-else-if="
                            selectedEmail && selectedEmail.status === 'opened'
                        "
                        class="email-engagement-alert"
                        style="background-color: #fef3c7; border-color: #fbbf24"
                    >
                        <i class="fa fa-eye" style="color: #d97706"></i>
                        <div>
                            <strong style="color: #92400e"
                                >The recipient has opened this email</strong
                            >
                            <p style="color: #78350f">
                                The email was opened but no reply has been
                                received yet. Consider following up if
                                appropriate.
                            </p>
                        </div>
                    </div>
                </div>

            <div class="modal-actions">
                <b-button
                    variant="outline-primary"
                    @click="$refs.viewEmailModal.hide()"
                >
                    Close
                </b-button>
            </div>
        </b-modal>

        <!-- Send Email Modal -->
        <b-modal
            id="sendEmailModal"
            ref="sendEmailModal"
            size="lg"
            centered
            hide-footer
            title="Send Follow-up Email"
            modal-class="center-title-modal leads-modal"
            @hidden="resetEmailForm"
        >
            <p class="modal-subtitle">
                You are sending a follow-up email to
                <strong>{{ lead.name }}</strong> (<span>{{ lead.email }}</span>)
            </p>

                <!-- Body -->
                <div
                    class="add-note-body"
                    :class="{ 'loading-overlay-container': isSendingEmail }"
                >
                    <div v-if="isSendingEmail" class="loading-overlay">
                        <atom-spinner
                            :animation-duration="1000"
                            :size="60"
                            color="#ff1d5e"
                        />
                    </div>
                    <!-- Caution -->
                    <div
                        v-if="lastEmailWarning"
                        class="email-caution"
                        :class="{
                            'email-caution-strong':
                                lastEmailWarning.type === 'strong',
                            'email-caution-soft':
                                lastEmailWarning.type === 'soft',
                        }"
                    >
                        <span class="email-caution-icon">{{
                            lastEmailWarning.icon
                        }}</span>
                        <div>
                            <strong>{{ lastEmailWarning.message }}</strong>
                            <p>{{ lastEmailWarning.description }}</p>
                        </div>
                    </div>

                    <b-form-group
                        label="Email Template (Optional)"
                        label-class="add-note-label"
                    >
                        <b-form-select
                            v-model="emailForm.template"
                            :options="templateOptions"
                            class="add-note-input"
                            @change="handleTemplateChange"
                            :disabled="isSendingEmail"
                        />
                    </b-form-group>

                    <b-form-group
                        label="Subject *"
                        label-class="add-note-label"
                    >
                        <b-form-input
                            v-model="emailForm.subject"
                            placeholder="Email subject"
                            class="add-note-input"
                            :disabled="isSendingEmail"
                        />
                    </b-form-group>

                    <b-form-group
                        label="Message *"
                        label-class="add-note-label"
                    >
                        <vue-editor
                            v-model="emailForm.message"
                            placeholder="Type your message here..."
                            class="email-editor"
                        ></vue-editor>
                    </b-form-group>
                </div>

            <div class="modal-actions">
                <b-button
                    variant="outline-primary"
                    @click="$refs.sendEmailModal.hide()"
                    :disabled="isSendingEmail"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="
                        !emailForm.subject || !emailForm.message || isSendingEmail
                    "
                    @click="sendEmailAction"
                >
                    <atom-spinner
                        v-if="isSendingEmail"
                        :animation-duration="1000"
                        :size="20"
                        color="#ffffff"
                        class="mr-2"
                    />
                    <i v-else class="fa fa-paper-plane mr-1"></i>
                    {{ isSendingEmail ? "Sending..." : "Send Email" }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>
<script>
import ComponentHeader from "@/components/componentNewHeader";
import { VueEditor } from "vue2-editor";
import { AtomSpinner } from "epic-spinners";

export default {
    name: "ViewLeads",
    components: {
        ComponentHeader,
        VueEditor,
        AtomSpinner,
    },
    data() {
        return {
            isLoading: false,
            isSavingNote: false,
            isSendingEmail: false,
            lead: {
                name: "",
                jobTitle: "",
                company: "",
                email: "",
                phone: "",
                source: "",
                originalSource: "",
                owner: "",
                status: "",
                created: "",
                subject: "",
                message: "",
                createdAt: "",
                _id: "",
            },
            communications: [],
            communicationCount: 0,
            emailForm: {
                template: "custom",
                subject: "",
                message: "",
            },
            templateOptions: [
                { value: "custom", text: "Custom Email" },
                { value: "introduction", text: "Introduction Email" },
                { value: "followup", text: "Follow-up Email" },
                { value: "demo", text: "Product Demo Invitation" },
            ],
            emailTemplates: {
                introduction: {
                    subject: "Introduction to Our Services",
                    body: `Dear {{customerName}},

I hope this email finds you well. I wanted to reach out to introduce our company and discuss how we can help {{companyName}} achieve its goals.

Would you be available for a quick call this week?

Best regards,
{{ownerName}}`,
                },
                followup: {
                    subject: "Following Up on Our Previous Conversation",
                    body: `Hi {{customerName}},

I wanted to follow up on our previous conversation about our services. Do you have any questions I can help answer?

Looking forward to hearing from you.

Best regards,
{{ownerName}}`,
                },
                demo: {
                    subject: "Product Demo Invitation",
                    body: `Dear {{customerName}},

Thank you for your interest in our product. I'd love to schedule a personalized demo to show you how we can help {{companyName}}.

Are you available this week for a 30-minute call?

Best regards,
{{ownerName}}`,
                },
            },
            noteForm: {
                note: "",
                nextAction: "",
                nextActionDate: "",
            },
            changeOwnerForm: {
                newOwner: "",
                reason: "",
                note: "",
            },
            ownerOptions: [
                { value: "", text: "Select new owner" },
                { value: "Jane Smith", text: "Jane Smith" },
                { value: "Sarah Williams", text: "Sarah Williams" },
                { value: "Mike Chen", text: "Mike Chen" },
            ],
            leadStats: {
                emailsSent: 2,
                notesAdded: 2,
                repliesReceived: 1,
            },
            reasonOptions: [
                { value: "", text: "Select reason" },
                { value: "workload balancing", text: "Workload Balancing" },
                {
                    value: "better expertise match",
                    text: "Better Expertise Match",
                },
                {
                    value: "territory reassignment",
                    text: "Territory Reassignment",
                },
                { value: "other", text: "Other" },
            ],
            selectedEmail: null,
        };
    },
    computed: {
        userName() {
            try {
                const userLogin = sessionStorage.getItem("userLogin");
                return userLogin ? JSON.parse(userLogin)["userName"] : "";
            } catch {
                return "";
            }
        },
        tenantId() {
            try {
                const userLogin = sessionStorage.getItem("userLogin");
                return userLogin ? JSON.parse(userLogin)["_id"] : "";
            } catch {
                return "";
            }
        },
        timelineItems() {
            const items = [];

            this.communications.forEach((comm) => {
                if (
                    comm.channel === "EMAIL" ||
                    comm.channel === "NOTE" ||
                    comm.direction === "INTERNAL"
                ) {
                    items.push({
                        ...comm,
                        isNote:
                            comm.channel === "NOTE" ||
                            comm.direction === "INTERNAL",
                        isEmail: comm.channel === "EMAIL",
                        timelineDate:
                            comm.sentAt || comm.createdAt || new Date(0),
                        _originalId: comm._id,
                    });
                }

                if (
                    comm.notes &&
                    Array.isArray(comm.notes) &&
                    comm.notes.length > 0
                ) {
                    comm.notes.forEach((note) => {
                        items.push({
                            ...note,
                            isNote: true,
                            isEmail: false,
                            channel: "NOTE",
                            direction: "INTERNAL",
                            timelineDate: note.createdAt || new Date(0),
                            _parentCommId: comm._id,
                        });
                    });
                }
            });

            // Sort all items by date (oldest first)
            return items.sort((a, b) => {
                const dateA = new Date(a.timelineDate || 0);
                const dateB = new Date(b.timelineDate || 0);
                return dateA - dateB;
            });
        },
        lastEmailWarning() {
            const outboundEmails = this.communications.filter(
                (comm) =>
                    comm.channel === "EMAIL" && comm.direction === "OUTBOUND",
            );

            if (!outboundEmails || outboundEmails.length === 0) {
                return null;
            }

            const lastSentEmail = [...outboundEmails].sort((a, b) => {
                const dateA = new Date(a.sentAt || a.createdAt || 0);
                const dateB = new Date(b.sentAt || b.createdAt || 0);
                return dateB - dateA;
            })[0];

            if (!lastSentEmail) {
                return null;
            }

            const lastSentTime = new Date(
                lastSentEmail.sentAt || lastSentEmail.createdAt,
            );
            const now = new Date();
            const hoursDiff = (now - lastSentTime) / (1000 * 60 * 60);

            if (hoursDiff < 1) {
                const minutesDiff = Math.round(hoursDiff * 60);
                return {
                    type: "strong",
                    icon: "❌",
                    message: `Warning: Last email sent ${minutesDiff} minute${minutesDiff !== 1 ? "s" : ""} ago`,
                    description:
                        "Multiple emails in short time may irritate the client. Consider waiting or adding a follow-up note instead.",
                };
            } else if (hoursDiff >= 1 && hoursDiff <= 4) {
                const hoursRounded = Math.round(hoursDiff * 10) / 10;
                return {
                    type: "soft",
                    icon: "⚠️",
                    message: `Caution: Last email sent ${hoursRounded} hour${hoursRounded !== 1 ? "s" : ""} ago`,
                    description:
                        "Consider spacing out your communications to maintain a professional relationship.",
                };
            } else {
                return null;
            }
        },
    },
    async mounted() {
        const leadId = this.$route.params.id;
        if (!leadId) {
            this.$toasted?.error("Lead ID not found");
            this.$router.push({ path: "/channels/leads" });
            return;
        }
        await this.fetchLeadData(leadId);
    },
    methods: {
        async refreshLead() {
            try {
                const leadId = this.$route.params.id;
                await this.fetchLeadData(leadId);
            } catch (error) {
                console.error("Failed to refresh lead data", error);
            }
        },
        async fetchLeadData(leadId) {
            this.isLoading = true;
            try {
                const response = await this.$store.dispatch(
                    "channel/viewLead",
                    leadId,
                );

                const responseData = response?.data || response;

                if (responseData) {
                    const { lead, communications, communicationCount } =
                        responseData;
                    this.lead = {
                        _id: lead._id || "",
                        name: lead.personal?.name || "",
                        email: lead.personal?.email || "",
                        phone: lead.personal?.phone || "",
                        company: lead.company?.name || "",
                        jobTitle: lead.company?.title || "",
                        source: this.formatSourceForDisplay(lead.source),
                        originalSource: lead.source || "",
                        status: this.formatStatusForDisplay(lead.status),
                        created: lead.createdAt || "",
                        createdAt: lead.createdAt || "",
                        subject: lead.subject || "",
                        message: lead.message || "",
                        owner: this.userName,
                    };

                    this.communications = (communications || [])
                        .map((comm) => {
                            if (comm.notes && Array.isArray(comm.notes)) {
                                comm.notes = [...comm.notes].sort((a, b) => {
                                    const dateA = new Date(a.createdAt || 0);
                                    const dateB = new Date(b.createdAt || 0);
                                    return dateA - dateB;
                                });
                            }
                            return comm;
                        })
                        .sort((a, b) => {
                            const dateA = new Date(
                                a.createdAt || a.sentAt || 0,
                            );
                            const dateB = new Date(
                                b.createdAt || b.sentAt || 0,
                            );
                            return dateA - dateB;
                        });
                    this.communicationCount = communicationCount || 0;

                    this.calculateStats();
                } else {
                    this.$toasted?.error("Failed to load lead data");
                    this.$router.push({ path: "/channels/leads" });
                }
            } catch (error) {
                console.error("Error fetching lead data:", error);
                this.$toasted?.error(
                    "Failed to load lead data. Please try again.",
                );
                this.$router.push({ path: "/channels/leads" });
            } finally {
                this.isLoading = false;
            }
        },
        formatSourceForDisplay(source) {
            if (!source) return "";
            const sourceMap = {
                CONTACT_FORM: "contact_form",
                SOCIAL: "social",
                MANUAL: "manual",
            };
            return sourceMap[source] || source.toLowerCase();
        },
        formatStatusForDisplay(status) {
            if (!status) return "";
            const statusMap = {
                NEW: "new",
                CONTACTED: "contacted",
                QUALIFIED: "qualified",
                CONVERTED: "converted",
                LOST: "lost",
            };
            return statusMap[status] || status.toLowerCase();
        },
        calculateStats() {
            let emailsSent = 0;
            let notesAdded = 0;
            let repliesReceived = 0;

            this.communications.forEach((comm) => {
                if (comm.channel === "EMAIL") {
                    if (comm.direction === "OUTBOUND") {
                        emailsSent++;
                    } else if (comm.direction === "INBOUND") {
                        repliesReceived++;
                    }
                } else if (
                    comm.channel === "NOTE" ||
                    comm.direction === "INTERNAL"
                ) {
                    notesAdded++;
                }
                if (comm.notes && Array.isArray(comm.notes)) {
                    notesAdded += comm.notes.length;
                }
            });

            this.leadStats = {
                emailsSent,
                notesAdded,
                repliesReceived,
            };
        },
        getInitials(name) {
            if (!name) return "";
            const parts = name.split(" ");
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },
        getStatusVariant(status) {
            const variants = {
                new: "primary",
                contacted: "warning",
                qualified: "success",
                converted: "primary",
                lost: "danger",
            };
            return variants[status] || "secondary";
        },
        capitalizeFirst(str) {
            if (!str) return "";
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        formatDate(dateStr) {
            if (!dateStr) return "";
            const date = new Date(dateStr);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        },
        formatDateTime(dateTimeStr) {
            if (!dateTimeStr) return "";
            const [datePart, timePart] = dateTimeStr.split(" ");
            const date = new Date(datePart + "T" + timePart);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        },
        formatDateTimeFromISO(isoString) {
            if (!isoString) return "";
            const date = new Date(isoString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        },
        formatDateOnlyFromISO(isoString) {
            if (!isoString) return "";
            const date = new Date(isoString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        },
        getNextActionDateStatus(dateString) {
            if (!dateString) return null;

            const actionDate = new Date(dateString);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            actionDate.setHours(0, 0, 0, 0);

            const diffTime = actionDate - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays < 0) {
                return {
                    status: "expired",
                    days: Math.abs(diffDays),
                    label: "Expired",
                };
            } else if (diffDays === 0) {
                return { status: "today", days: 0, label: "Due Today" };
            } else if (diffDays === 1) {
                return { status: "tomorrow", days: 1, label: "Due Tomorrow" };
            } else if (diffDays <= 3) {
                return {
                    status: "soon",
                    days: diffDays,
                    label: `Due in ${diffDays} days`,
                };
            }

            return null;
        },
        formatSource(source) {
            if (!source) return "";
            return source
                .split("_")
                .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
                .join(" ");
        },
        addNote() {
            // Open add note modal
            this.resetNoteForm();
            this.$refs.addNoteModal.show();
        },
        async saveNote() {
            // Validate required fields
            if (!this.noteForm.note) {
                this.$toasted?.error("Follow-up Note is required.");
                return;
            }

            // Get the last communication
            const lastCommunication =
                this.communications && this.communications.length > 0
                    ? this.communications[this.communications.length - 1]
                    : null;

            if (!lastCommunication || !lastCommunication._id) {
                this.$toasted?.error("Last communication not found.");
                return;
            }

            const payload = {
                id: lastCommunication._id,
                notes: [
                    {
                        message: this.noteForm.note,
                        visibility: "INTERNAL",
                        nextAction: this.noteForm.nextAction || "",
                        nextActionDate: this.noteForm.nextActionDate || "",
                    },
                ],
            };

            this.isSavingNote = true;
            try {
                const response = await this.$store.dispatch(
                    "channel/addNotesAction",
                    payload,
                );

                if (response?.status === 0) {
                    this.$toasted?.error(
                        response.message || "Failed to save note",
                    );
                    return;
                }

                this.$toasted?.success("Note saved successfully.");
                this.$refs.addNoteModal.hide();
                this.resetNoteForm();

                // Refresh lead data to show the new note
                await this.fetchLeadData(this.lead._id);
            } catch (error) {
                console.error("Error saving note:", error);
                this.$toasted?.error("Failed to save note. Please try again.");
            } finally {
                this.isSavingNote = false;
            }
        },
        resetNoteForm() {
            this.noteForm = {
                note: "",
                nextAction: "",
                nextActionDate: "",
            };
        },
        scheduleMeeting() {
            // Placeholder
            console.log("Schedule meeting");
        },
        sendEmail() {
            // Open send email modal
            this.resetEmailForm();
            this.$refs.sendEmailModal.show();
        },
        handleTemplateChange(templateValue) {
            if (templateValue === "custom") {
                this.emailForm.subject = "";
                this.emailForm.message = "";
                return;
            }

            const template = this.emailTemplates[templateValue];
            if (!template) return;

            const customerName = this.lead.name || "Customer";
            const ownerName = this.userName || "Team";
            const companyName = this.lead.company || "your company";

            let subject = template.subject
                .replace(/\{\{customerName\}\}/g, customerName)
                .replace(/\{\{ownerName\}\}/g, ownerName)
                .replace(/\{\{companyName\}\}/g, companyName);

            let body = template.body
                .replace(/\{\{customerName\}\}/g, customerName)
                .replace(/\{\{ownerName\}\}/g, ownerName)
                .replace(/\{\{companyName\}\}/g, companyName);

            body = body
                .split("\n")
                .filter((line) => line.trim() !== "")
                .map((line) => `<p>${line.trim()}</p>`)
                .join("");

            this.emailForm.subject = subject;
            this.emailForm.message = body;
        },
        async sendEmailAction() {
            // Validate required fields
            if (!this.emailForm.subject || !this.emailForm.message) {
                this.$toasted?.error("Subject and Message are required.");
                return;
            }

            if (!this.lead._id) {
                this.$toasted?.error("Lead ID not found.");
                return;
            }

            if (!this.lead.email) {
                this.$toasted?.error("Lead email not found.");
                return;
            }

            if (!this.tenantId) {
                this.$toasted?.error("Tenant ID not found.");
                return;
            }

            const payload = {
                id: this.lead._id,
                tenant: this.tenantId,
                to: this.lead.email,
                subject: this.emailForm.subject,
                message: this.emailForm.message,
            };

            this.isSendingEmail = true;
            try {
                const response = await this.$store.dispatch(
                    "channel/sendEmailAction",
                    payload,
                );

                if (response?.status === 0) {
                    this.$toasted?.error(
                        response.message || "Failed to send email",
                    );
                    return;
                }

                this.$toasted?.success("Email sent successfully.");
                this.$refs.sendEmailModal.hide();
                this.resetEmailForm();

                await this.fetchLeadData(this.lead._id);
            } catch (error) {
                console.error("Error sending email:", error);
                this.$toasted?.error("Failed to send email. Please try again.");
            } finally {
                this.isSendingEmail = false;
            }
        },
        resetEmailForm() {
            this.emailForm = {
                template: "custom",
                subject: "",
                message: "",
            };
        },
        openChangeOwnerModal() {
            // Open change owner modal
            this.resetChangeOwnerForm();
            this.$refs.changeOwnerModal.show();
        },
        changeOwnerAction() {
            // Validate required fields
            if (
                !this.changeOwnerForm.newOwner ||
                !this.changeOwnerForm.reason
            ) {
                this.$toasted?.error(
                    "New Owner and Reason for Change are required.",
                );
                return;
            }

            // Update lead owner
            this.lead.owner = this.changeOwnerForm.newOwner;

            // Simulate change
            this.$toasted?.success("Lead owner changed successfully.");
            this.$refs.changeOwnerModal.hide();
            this.resetChangeOwnerForm();
        },
        resetChangeOwnerForm() {
            this.changeOwnerForm = {
                newOwner: "",
                reason: "",
                note: "",
            };
        },
        getEmailPreview(htmlContent) {
            if (!htmlContent) return "";
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = htmlContent;
            const text = tempDiv.textContent || tempDiv.innerText || "";
            return text.length > 150 ? text.substring(0, 150) + "..." : text;
        },
        formatEmailBody(body) {
            if (!body) return "";
            const isFullHtmlDocument =
                /<!DOCTYPE\s+html/i.test(body) ||
                (/<html/i.test(body) && /<body/i.test(body));

            if (isFullHtmlDocument) {
                const bodyMatch = body.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                if (bodyMatch && bodyMatch[1]) {
                    let bodyContent = bodyMatch[1].trim();
                    bodyContent = bodyContent.replace(
                        /<script[\s\S]*?<\/script>/gi,
                        "",
                    );
                    bodyContent = bodyContent.replace(
                        /<style[\s\S]*?<\/style>/gi,
                        "",
                    );
                    return `<div class="email-body-content email-html-content">${bodyContent}</div>`;
                } else {
                    let sanitized = body;
                    sanitized = sanitized.replace(
                        /<script[\s\S]*?<\/script>/gi,
                        "",
                    );
                    sanitized = sanitized.replace(
                        /<style[\s\S]*?<\/style>/gi,
                        "",
                    );
                    return `<div class="email-body-content email-html-content">${sanitized}</div>`;
                }
            }
            const hasHtmlStructure =
                /<[a-z][a-z0-9]*[\s>\/]/i.test(body) &&
                (body.includes("</") ||
                    /<[a-z][a-z0-9]*\s+[a-z-]+=/i.test(body) ||
                    /<(div|p|span|table|tr|td|a|img|br|hr|strong|em|ul|ol|li)/i.test(
                        body,
                    ));

            const hasUrlInBrackets = /<https?:\/\/[^>]+>/i.test(body);
            const hasQuotedReplies = /^\s*>/m.test(body);
            const startsWithHtmlTag = /^\s*<[a-z][a-z0-9]*[\s>\/]/i.test(
                body.trim(),
            );

            const isPlainText =
                hasQuotedReplies ||
                (hasUrlInBrackets && !hasHtmlStructure) ||
                (!hasHtmlStructure &&
                    body.includes("\n") &&
                    !startsWithHtmlTag);

            if (isPlainText) {
                let preprocessedBody = body;
                const socialMediaMap = new Map();
                let socialMediaCounter = 0;
                preprocessedBody = preprocessedBody.replace(
                    /\[image:\s*((?:[\s\S](?!\]))+?)(?:\s*icon\s*)?\]\s*(?:\r?\n\s*)*<https?:\/\/([^>\s]+)>/gi,
                    (match, platform, url) => {
                        platform = platform
                            .replace(/\s*icon\s*$/i, "")
                            .replace(/\r?\n+/g, " ")
                            .trim();
                        const platformLower = platform.toLowerCase();
                        let iconClass = "fa-link";
                        let platformName = platform;
                        if (
                            platformLower.includes("facebook") ||
                            platformLower.includes("fb")
                        ) {
                            iconClass = "fa-facebook";
                            platformName = "Facebook";
                        } else if (
                            platformLower.includes("youtube") ||
                            platformLower.includes("yt")
                        ) {
                            iconClass = "fa-youtube";
                            platformName = "YouTube";
                        } else if (
                            platformLower.includes("linkedin") ||
                            platformLower.includes("linked")
                        ) {
                            iconClass = "fa-linkedin";
                            platformName = "LinkedIn";
                        } else if (
                            platformLower.includes("instagram") ||
                            platformLower.includes("insta") ||
                            platformLower.includes("ig")
                        ) {
                            iconClass = "fa-instagram";
                            platformName = "Instagram";
                        } else if (
                            platformLower.includes("twitter") ||
                            platformLower.includes("x.com")
                        ) {
                            iconClass = "fa-twitter";
                            platformName = "Twitter";
                        } else if (platformLower.includes("github")) {
                            iconClass = "fa-github";
                            platformName = "GitHub";
                        } else if (platformLower.includes("whatsapp")) {
                            iconClass = "fa-whatsapp";
                            platformName = "WhatsApp";
                        } else if (platformLower.includes("pinterest")) {
                            iconClass = "fa-pinterest";
                            platformName = "Pinterest";
                        }

                        const fullUrl = `https://${url}`;
                        const placeholder = `__SOCIAL_MEDIA_${socialMediaCounter++}__`;
                        const html = `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                        socialMediaMap.set(placeholder, html);
                        return placeholder;
                    },
                );

                const lines = preprocessedBody.split(/\r?\n/);
                const processedParts = [];
                let currentQuotedBlock = [];
                let currentRegularBlock = [];

                lines.forEach((line) => {
                    const trimmedLine = line.trim();
                    const isQuoted = /^(&gt;|>)/.test(trimmedLine);

                    if (isQuoted) {
                        if (currentRegularBlock.length > 0) {
                            processedParts.push({
                                type: "regular",
                                content: currentRegularBlock.join("\n"),
                            });
                            currentRegularBlock = [];
                        }
                        const cleanLine = trimmedLine.replace(
                            /^(&gt;|>)\s*/,
                            "",
                        );
                        currentQuotedBlock.push(cleanLine || " ");
                    } else {
                        if (currentQuotedBlock.length > 0) {
                            processedParts.push({
                                type: "quoted",
                                content: currentQuotedBlock.join("\n"),
                            });
                            currentQuotedBlock = [];
                        }
                        currentRegularBlock.push(line);
                    }
                });

                if (currentRegularBlock.length > 0) {
                    processedParts.push({
                        type: "regular",
                        content: currentRegularBlock.join("\n"),
                    });
                }
                if (currentQuotedBlock.length > 0) {
                    processedParts.push({
                        type: "quoted",
                        content: currentQuotedBlock.join("\n"),
                    });
                }

                let formattedBody = "";

                processedParts.forEach((part) => {
                    let partContent = part.content;
                    const linkPlaceholders = [];
                    let placeholderIndex = 0;
                    partContent = partContent.replace(
                        /<https?:\/\/[^>\s]+>/gi,
                        (match) => {
                            const url = match.slice(1, -1);
                            const placeholder = `__LINK_PLACEHOLDER_${placeholderIndex++}__`;
                            linkPlaceholders.push(
                                `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
                            );
                            return placeholder;
                        },
                    );

                    if (part.type === "quoted") {
                        partContent = partContent
                            .replace(/&/g, "&amp;")
                            .replace(/</g, "&lt;")
                            .replace(/>/g, "&gt;");

                        socialMediaMap.forEach((html, placeholder) => {
                            partContent = partContent.replace(
                                placeholder,
                                html,
                            );
                        });

                        linkPlaceholders.forEach((link, index) => {
                            partContent = partContent.replace(
                                `__LINK_PLACEHOLDER_${index}__`,
                                link,
                            );
                        });

                        partContent = partContent.replace(/\n/g, "<br>");
                        formattedBody += `<div class="email-quoted-reply">${partContent}</div>`;
                    } else {
                        partContent = partContent
                            .replace(/&/g, "&amp;")
                            .replace(/</g, "&lt;")
                            .replace(/>/g, "&gt;");
                        socialMediaMap.forEach((html, placeholder) => {
                            partContent = partContent.replace(
                                placeholder,
                                html,
                            );
                        });
                        linkPlaceholders.forEach((link, index) => {
                            partContent = partContent.replace(
                                `__LINK_PLACEHOLDER_${index}__`,
                                link,
                            );
                        });
                        partContent = partContent.replace(/\n/g, "<br>");
                        partContent = partContent.replace(
                            /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi,
                            (match, url) => {
                                const beforeMatch = partContent.substring(
                                    0,
                                    partContent.indexOf(match),
                                );
                                const afterMatch = partContent.substring(
                                    partContent.indexOf(match) + match.length,
                                );
                                const lastOpenTag =
                                    beforeMatch.lastIndexOf("<a");
                                const lastCloseTag =
                                    beforeMatch.lastIndexOf("</a>");
                                if (lastOpenTag > lastCloseTag) {
                                    return match;
                                }
                                if (
                                    match.includes("__SOCIAL_MEDIA_") ||
                                    match.includes("__LINK_PLACEHOLDER_")
                                ) {
                                    return match;
                                }

                                return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
                            },
                        );

                        partContent = partContent.replace(
                            /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
                            '<a href="mailto:$1">$1</a>',
                        );

                        formattedBody += partContent;
                    }
                });
                const getSocialMediaInfo = (platform) => {
                    const platformLower = platform.toLowerCase();
                    let iconClass = "fa-link";
                    let platformName = platform;

                    if (
                        platformLower.includes("facebook") ||
                        platformLower.includes("fb")
                    ) {
                        iconClass = "fa-facebook";
                        platformName = "Facebook";
                    } else if (
                        platformLower.includes("youtube") ||
                        platformLower.includes("yt")
                    ) {
                        iconClass = "fa-youtube";
                        platformName = "YouTube";
                    } else if (
                        platformLower.includes("linkedin") ||
                        platformLower.includes("linked")
                    ) {
                        iconClass = "fa-linkedin";
                        platformName = "LinkedIn";
                    } else if (
                        platformLower.includes("instagram") ||
                        platformLower.includes("insta") ||
                        platformLower.includes("ig")
                    ) {
                        iconClass = "fa-instagram";
                        platformName = "Instagram";
                    } else if (
                        platformLower.includes("twitter") ||
                        platformLower.includes("x.com")
                    ) {
                        iconClass = "fa-twitter";
                        platformName = "Twitter";
                    } else if (platformLower.includes("github")) {
                        iconClass = "fa-github";
                        platformName = "GitHub";
                    } else if (platformLower.includes("whatsapp")) {
                        iconClass = "fa-whatsapp";
                        platformName = "WhatsApp";
                    } else if (platformLower.includes("pinterest")) {
                        iconClass = "fa-pinterest";
                        platformName = "Pinterest";
                    }
                    return { iconClass, platformName, platformLower };
                };
                formattedBody = formattedBody.replace(
                    /(<div class="email-quoted-reply">)([\s\S]*?)(<\/div>)/gi,
                    (match, openDiv, content, closeDiv) => {
                        let processedContent = content;
                        processedContent = processedContent.replace(
                            /\[image:\s*([^\]]+?)\s*(?:icon)?\]\s*(?:&lt;br\s*\/?&gt;)?\s*&lt;https?:\/\/([^&>]+)&gt;/gi,
                            (m, platform, url) => {
                                const {
                                    iconClass,
                                    platformName,
                                    platformLower,
                                } = getSocialMediaInfo(platform);
                                const fullUrl = `https://${url}`;
                                return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                            },
                        );
                        processedContent = processedContent.replace(
                            /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)\s+(?:target=["']_blank["']|target=&quot;_blank&quot;)\s+(?:rel=["']noopener noreferrer["']|rel=&quot;noopener noreferrer&quot;)\s+(?:class=["']social-media-link[^"']*["']|class=&quot;social-media-link[^&]*&quot;)(?:&gt;|>)([^<]+)/gi,
                            (m, url, linkText) => {
                                let platform = "link";
                                if (url.includes("facebook.com"))
                                    platform = "facebook";
                                else if (
                                    url.includes("youtube.com") ||
                                    url.includes("youtu.be")
                                )
                                    platform = "youtube";
                                else if (url.includes("linkedin.com"))
                                    platform = "linkedin";
                                else if (url.includes("instagram.com"))
                                    platform = "instagram";
                                else if (
                                    url.includes("twitter.com") ||
                                    url.includes("x.com")
                                )
                                    platform = "twitter";

                                const {
                                    iconClass,
                                    platformName,
                                    platformLower,
                                } = getSocialMediaInfo(platform);
                                return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                            },
                        );
                        processedContent = processedContent.replace(
                            /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)(?:\s+)?(?:target=|&quot;target=)([^>\s]+)(?:\s+)?(?:rel=|&quot;rel=)([^>\s]+)(?:\s+)?(?:class=|&quot;class=)([^>\s]+)(?:&gt;|>)([^<]+)/gi,
                            (m, url) => {
                                let platform = "link";
                                if (url.includes("facebook.com"))
                                    platform = "facebook";
                                else if (
                                    url.includes("youtube.com") ||
                                    url.includes("youtu.be")
                                )
                                    platform = "youtube";
                                else if (url.includes("linkedin.com"))
                                    platform = "linkedin";
                                else if (url.includes("instagram.com"))
                                    platform = "instagram";
                                else if (
                                    url.includes("twitter.com") ||
                                    url.includes("x.com")
                                )
                                    platform = "twitter";

                                const {
                                    iconClass,
                                    platformName,
                                    platformLower,
                                } = getSocialMediaInfo(platform);
                                return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                            },
                        );
                        processedContent = processedContent.replace(
                            /\[image:\s*([^\]]+?)\s*(?:icon)?\]\s*(?:<br\s*\/?>|\s+)*<https?:\/\/([^>\s]+)>/gi,
                            (m, platform, url) => {
                                const {
                                    iconClass,
                                    platformName,
                                    platformLower,
                                } = getSocialMediaInfo(platform);
                                const fullUrl = `https://${url}`;
                                return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                            },
                        );
                        processedContent = processedContent.replace(
                            /\[image:\s*([^\]]+?)\s*(?:icon)?\]\s*(?:&lt;br\s*\/?&gt;|\s+)*&lt;https?:\/\/([^&>]+)&gt;/gi,
                            (m, platform, url) => {
                                const {
                                    iconClass,
                                    platformName,
                                    platformLower,
                                } = getSocialMediaInfo(platform);
                                const fullUrl = `https://${url}`;
                                return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                            },
                        );

                        return openDiv + processedContent + closeDiv;
                    },
                );
                formattedBody = formattedBody.replace(
                    /\[image:\s*([^\]]+?)\s*(?:icon)?\]\s*(?:&lt;br\s*\/?&gt;|\s+)*&lt;https?:\/\/([^&>]+)&gt;/gi,
                    (match, platform, url) => {
                        const { iconClass, platformName, platformLower } =
                            getSocialMediaInfo(platform);
                        const fullUrl = `https://${url}`;
                        return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                    },
                );
                formattedBody = formattedBody.replace(
                    /\[image:\s*([^\]]+?)\s*(?:icon)?\]\s*(?:<br\s*\/?>|\s+)*<a[^>]*href=["'](https?:\/\/[^"']+)["'][^>]*>([^<]*)<\/a>/gi,
                    (match, platform, url, linkText) => {
                        const { iconClass, platformName, platformLower } =
                            getSocialMediaInfo(platform);
                        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                    },
                );
                formattedBody = formattedBody.replace(
                    /\[image:\s*([^\]]+?)\s*(?:icon)?\]\s*(?:<br\s*\/?>|\s+)*<https?:\/\/([^>\s]+)>/gi,
                    (match, platform, url) => {
                        const { iconClass, platformName, platformLower } =
                            getSocialMediaInfo(platform);
                        const fullUrl = `https://${url}`;
                        return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                    },
                );
                formattedBody = formattedBody.replace(
                    /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)"\s+target=["']_blank["']\s+rel=["']noopener noreferrer["']\s+class=["']social-media-link\s+social-([^"']+)["']\s*&gt;\s*([^<]+)/gi,
                    (match, url, platformClass, linkText) => {
                        const platform = platformClass
                            .replace("social-", "")
                            .trim();
                        const { iconClass, platformName, platformLower } =
                            getSocialMediaInfo(platform);
                        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                    },
                );

                formattedBody = formattedBody.replace(
                    /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)&quot;\s+target=&quot;_blank&quot;\s+rel=&quot;noopener noreferrer&quot;\s+class=&quot;social-media-link\s+social-([^&]+)&quot;\s*&gt;\s*([^<]+)/gi,
                    (match, url, platformClass, linkText) => {
                        const platform = platformClass
                            .replace("social-", "")
                            .trim();
                        const { iconClass, platformName, platformLower } =
                            getSocialMediaInfo(platform);
                        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                    },
                );
                formattedBody = formattedBody.replace(
                    /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)(?:\s+)?(?:target=["']_blank["']|target=&quot;_blank&quot;)\s+(?:rel=["']noopener noreferrer["']|rel=&quot;noopener noreferrer&quot;)\s+(?:class=["']social-media-link[^"']*["']|class=&quot;social-media-link[^&]*&quot;)(?:\s*&gt;|>)([^<]+)/gi,
                    (match, url, linkText) => {
                        let platform = "link";
                        if (url.includes("facebook.com")) platform = "facebook";
                        else if (
                            url.includes("youtube.com") ||
                            url.includes("youtu.be")
                        )
                            platform = "youtube";
                        else if (url.includes("linkedin.com"))
                            platform = "linkedin";
                        else if (url.includes("instagram.com"))
                            platform = "instagram";
                        else if (
                            url.includes("twitter.com") ||
                            url.includes("x.com")
                        )
                            platform = "twitter";

                        const { iconClass, platformName, platformLower } =
                            getSocialMediaInfo(platform);
                        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="social-media-link social-${platformLower.replace(/\s+/g, "-")}"><i class="fa ${iconClass}"></i> <span>${platformName}</span></a>`;
                    },
                );

                formattedBody = formattedBody.replace(
                    /\[image:\s*([^\]]+)\]/gi,
                    '<span class="email-image-placeholder"><i class="fa fa-image"></i> $1</span>',
                );
                formattedBody = formattedBody.replace(
                    /(<br>\s*){3,}/gi,
                    "<br><br>",
                );

                return `<div class="email-body-content">${formattedBody}</div>`;
            }
            let sanitized = body.replace(/<script[\s\S]*?<\/script>/gi, "");
            sanitized = sanitized.replace(/<style[\s\S]*?<\/style>/gi, "");
            return `<div class="email-body-content email-html-content">${sanitized}</div>`;
        },
        viewFullEmail(emailData) {
            this.selectedEmail = emailData;
            this.$refs.viewEmailModal.show();
        },
    },
};
</script>
<style scoped>
/* Right side stays pushed */
.lead-header-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}

.lead-subtitle-line {
    margin-top: 6px;
    color: var(--slate-500, #64748b);
    font-size: 12px;
    font-weight: 600;
}

.owner-card {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 35px;
    width: 130px;
    padding: 8px 10px;
    background-color: var(--info-bg, #eff6ff);
    border: 1px solid var(--slate-200, #e2e8f0);
}

.owner-avatar {
    width: 23px;
    height: 23px;
    border-radius: 50%;
    background-color: var(--primary, #2563eb);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 11px;
}

.owner-meta {
    display: flex;
    flex-direction: column;
    justify-content: center;
    line-height: 1;
}

/* Owner text */
.owner-name {
    font-weight: 600;
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
}

.owner-role {
    font-size: 10px;
    color: #64748b;
    white-space: nowrap;
    margin-top: 2px;
}

.change-owner-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid #e5e7eb;
    background-color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    color: #0f172a;
    cursor: pointer;
}

.change-owner-btn:focus,
.change-owner-btn:focus-visible {
    outline: none !important;
    box-shadow: none !important;
}

.change-owner-btn:hover {
    background-color: #f8fafc;
}

.status-strip {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    column-gap: 12px;

    padding: 12px 16px;
    background-color: #ecfdf3;
    border: 1px solid #d1fadf;
    border-radius: 10px;
}

.status-left {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    column-gap: 8px;
}

.status-icon {
    color: #22c55e;
    font-size: 1.1rem;
}

.status-text {
    font-size: 0.9rem;
    color: #065f46;
    line-height: 1.4;
}

.status-pill {
    display: inline-block;
    justify-self: end;
    width: max-content;
    min-width: unset;
    max-width: unset;
    background-color: #e0e7ff;
    color: #1e40af;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 4px 14px;
    border-radius: 999px;
    text-transform: capitalize;
    white-space: nowrap;
}

.quick-actions {
    display: flex;
    flex-direction: column;
}

.info-item {
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e9ecef;
}

.info-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.info-label {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
    font-weight: 500;
}

.info-value {
    font-size: 0.95rem;
    color: #212529;
}

.detail-item {
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e9ecef;
}

.detail-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.detail-label {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
    font-weight: 500;
}

.detail-value {
    font-size: 0.95rem;
    color: #212529;
}

.team-access {
    display: grid;
    row-gap: 16px;
}

/* Each row */
.team-member {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    column-gap: 12px;
}

/* Avatar */
.team-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: #2563eb;
    color: #ffffff;

    display: grid;
    place-items: center;

    font-weight: 600;
    font-size: 0.8rem;
}

/* Name + access */
.team-info {
    display: grid;
    row-gap: 2px;
}

.team-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #0f172a;
}

.team-access-level {
    font-size: 0.8rem;
    color: #64748b;
}

/* Role badge */
.team-role-badge {
    font-size: 0.75rem;
    padding: 4px 10px;
    border-radius: 999px;
    white-space: nowrap;
}

.timeline-wrapper {
    position: relative;
    padding-left: 60px;
}

/* .timeline-wrapper::before {
    content: "";
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
} */

.timeline-card {
    position: relative;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 20px;
    padding: 20px;
    display: flex;
    align-items: flex-start;
}
.timeline-line {
    position: absolute;
    left: -30px;
    top: 60px;
    width: 2px;
    height: calc(100% - 60px);
    background: #e5e7eb;
}

.timeline-card:last-child .timeline-line {
    display: none;
}

.timeline-card.note-card {
    background: #ffffff;
}

.timeline-card.email-card {
    background: #ffffff;
}

.timeline-card.contact-form-card {
    background: #f0f9ff;
}

.timeline-icon-wrapper {
    position: absolute;
    left: -50px;
    top: 20px;
    z-index: 2;
}

.timeline-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.timeline-icon.note-icon {
    background: #e9d5ff;
    color: #9333ea;
    border: 2px solid #9333ea;
}

.timeline-icon.email-icon {
    background: #dbeafe;
    color: #3b82f6;
    border: 2px solid #3b82f6;
}

.timeline-icon.contact-form-icon {
    background: #0ea5e9;
    color: #ffffff;
}

.timeline-card-content {
    flex: 1;
    width: 100%;
}

.timeline-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 8px;
}

.timeline-type {
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 4px 10px;
    border-radius: 4px;
    display: inline-block;
}

.timeline-type.note-type {
    color: #9333ea;
    background-color: rgba(147, 51, 234, 0.1);
}

.timeline-type.email-type {
    color: #3b82f6;
    background-color: rgba(59, 130, 246, 0.1);
}

.timeline-type.contact-form-type {
    color: #0ea5e9;
}

.timeline-meta-text {
    font-size: 0.8rem;
    color: #6b7280;
}

.timeline-body {
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 12px;
}

.timeline-body.note-body {
    color: #4b5563;
}

.timeline-body.email-body {
    color: #4b5563;
}

.timeline-body.email-body-html {
    word-wrap: break-word;
}

.timeline-body.email-body-html ::v-deep p {
    margin-bottom: 0.5rem;
    line-height: 1.6;
}

.timeline-body.email-body-html ::v-deep p:last-child {
    margin-bottom: 0;
}

.timeline-body.email-body-html ::v-deep strong {
    font-weight: 600;
}

.timeline-body.email-body-html ::v-deep em {
    font-style: italic;
}

.timeline-body.email-body-html ::v-deep ul,
.timeline-body.email-body-html ::v-deep ol {
    margin-left: 1.5rem;
    margin-bottom: 0.5rem;
}

.timeline-body.email-body-html ::v-deep li {
    margin-bottom: 0.25rem;
}

.email-message-html {
    background-color: #f8f9fa;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
    word-wrap: break-word;
    color: #4b5563;
    line-height: 1.6;
}

.email-message-html ::v-deep p {
    margin-bottom: 0.75rem;
    line-height: 1.6;
}

.email-message-html ::v-deep p:last-child {
    margin-bottom: 0;
}

.email-message-html ::v-deep strong {
    font-weight: 600;
    color: #111827;
}

.email-message-html ::v-deep em {
    font-style: italic;
}

.email-message-html ::v-deep ul,
.email-message-html ::v-deep ol {
    margin-left: 1.5rem;
    margin-bottom: 0.75rem;
}

.email-message-html ::v-deep li {
    margin-bottom: 0.5rem;
}

.email-message-html ::v-deep a {
    color: #3b82f6;
    text-decoration: underline;
}

.email-message-html ::v-deep a:hover {
    color: #2563eb;
}

.email-message-html ::v-deep .email-body-content {
    white-space: pre-wrap;
    word-wrap: break-word;
}

.email-message-html ::v-deep .email-quoted-reply {
    background-color: #f1f5f9;
    border-left: 3px solid #94a3b8;
    padding: 8px 12px;
    margin: 8px 0;
    color: #64748b;
    font-size: 0.9em;
    border-radius: 4px;
    white-space: pre-wrap;
    word-wrap: break-word;
}

.email-message-html ::v-deep .email-quoted-reply br {
    display: block;
    content: "";
    margin-top: 4px;
}

.email-message-html ::v-deep .email-image-placeholder {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background-color: #e5e7eb;
    border-radius: 4px;
    color: #6b7280;
    font-size: 0.9em;
    margin: 4px 0;
}

.email-message-html ::v-deep .email-image-placeholder i {
    color: #9ca3af;
}

.email-message-html ::v-deep .social-media-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    margin: 4px 8px 4px 0;
    background-color: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    color: #374151;
    text-decoration: none;
    font-size: 0.9em;
    font-weight: 500;
    transition: all 0.2s ease;
    vertical-align: middle;
}

.email-message-html ::v-deep .social-media-link:hover {
    background-color: #e5e7eb;
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.email-message-html ::v-deep .social-media-link i {
    font-size: 1.1em;
    width: 18px;
    text-align: center;
}

.email-message-html ::v-deep .social-media-link span {
    font-weight: 500;
}

.email-message-html ::v-deep .social-facebook {
    background-color: #e7f3ff;
    border-color: #b3d9ff;
    color: #1877f2;
}

.email-message-html ::v-deep .social-facebook:hover {
    background-color: #d0e7ff;
    border-color: #80bfff;
}

.email-message-html ::v-deep .social-youtube {
    background-color: #fee;
    border-color: #fcc;
    color: #ff0000;
}

.email-message-html ::v-deep .social-youtube:hover {
    background-color: #fdd;
    border-color: #faa;
}

.email-message-html ::v-deep .social-linkedin {
    background-color: #e6f2f9;
    border-color: #b3d9e6;
    color: #0077b5;
}

.email-message-html ::v-deep .social-linkedin:hover {
    background-color: #cce5f2;
    border-color: #80c2d9;
}

.email-message-html ::v-deep .social-instagram {
    background: linear-gradient(135deg, #fef0f0 0%, #f0e6ff 100%);
    border-color: #e0c2ff;
    color: #e4405f;
}

.email-message-html ::v-deep .social-instagram:hover {
    background: linear-gradient(135deg, #fde0e0 0%, #e0c2ff 100%);
    border-color: #c299ff;
}

.email-message-html ::v-deep .email-html-content {
    white-space: normal;
}

.email-message-html ::v-deep .email-html-content table {
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
}

.email-message-html ::v-deep .email-html-content img {
    max-width: 100%;
    height: auto;
    display: block;
}

.email-message-html ::v-deep .email-html-content table td {
    padding: 8px;
}

.email-message-html ::v-deep .email-html-content a {
    color: #3b82f6;
    text-decoration: underline;
}

.email-message-html ::v-deep .email-html-content a:hover {
    color: #2563eb;
}

.timeline-next-action {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: #dbeafe;
    padding: 12px 16px;
    border-radius: 8px;
    margin-top: 12px;
}

.timeline-next-action i {
    color: #3b82f6;
    font-size: 1rem;
    margin-top: 2px;
}

.next-action-label {
    font-weight: 600;
    font-size: 0.85rem;
    color: #1e40af;
    margin-bottom: 4px;
}

.next-action-text {
    font-size: 0.9rem;
    color: #1e3a8a;
    margin-bottom: 4px;
}

.next-action-due {
    font-size: 0.8rem;
    color: #64748b;
}

.email-subject-title {
    font-weight: 700;
    font-size: 1rem;
    color: #111827;
    margin-bottom: 12px;
}

.email-content-preview {
    background-color: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 12px;
    color: #4b5563;
    font-size: 0.9rem;
    line-height: 1.6;
    max-height: 100px;
    overflow: hidden;
}

.note-content-box {
    background-color: #f3e8ff;
    border: 1px solid #e9d5ff;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 12px;
    color: #4b5563;
    font-size: 0.9rem;
    line-height: 1.6;
}

.note-next-action-box {
    background-color: #dbeafe;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 12px 16px;
    margin-top: 12px;
    transition: all 0.3s ease;
}

.note-next-action-box.next-action-expired {
    background-color: #fee2e2;
    border: 1px solid #fca5a5;
    animation: pulse-expired 2s ease-in-out infinite;
}

.note-next-action-box.next-action-urgent {
    background-color: #fef3c7;
    border: 1px solid #fbbf24;
    animation: pulse-urgent 2s ease-in-out infinite;
}

.note-next-action-box.next-action-soon {
    background-color: #dbeafe;
    border: 1px solid #60a5fa;
}

@keyframes pulse-expired {
    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
    }
    50% {
        box-shadow: 0 0 0 4px rgba(239, 68, 68, 0);
    }
}

@keyframes pulse-urgent {
    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4);
    }
    50% {
        box-shadow: 0 0 0 4px rgba(245, 158, 11, 0);
    }
}

.next-action-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #1e40af;
    flex-wrap: wrap;
    gap: 8px;
}

.next-action-header i {
    color: #3b82f6;
}

.next-action-status-badge {
    font-size: 0.7rem;
    padding: 3px 8px;
    font-weight: 600;
    border-radius: 12px;
}

.next-action-text {
    font-size: 0.9rem;
    color: #1e3a8a;
    margin-bottom: 6px;
    line-height: 1.5;
}

.next-action-due {
    font-size: 0.85rem;
    color: #64748b;
    font-weight: 500;
}

.next-action-due.text-danger {
    color: #dc2626;
    font-weight: 600;
}

.next-action-due.text-warning {
    color: #d97706;
    font-weight: 600;
}

.next-action-due.text-info {
    color: #0284c7;
    font-weight: 600;
}

.contact-form-subject {
    font-weight: 600;
    font-size: 0.95rem;
    color: #0f172a;
    margin-bottom: 8px;
}

.contact-form-subject strong {
    color: #64748b;
    font-weight: 600;
    margin-right: 6px;
}

.timeline-body.contact-form-body {
    color: #4b5563;
}

.source-badge {
    background-color: #dbeafe;
    color: #1e40af;
    border: none;
    font-size: 0.75rem;
    padding: 4px 8px;
    font-weight: 500;
}

.email-status-badge {
    background-color: #fee2e2;
    color: #991b1b;
    border: none;
    font-size: 0.75rem;
    padding: 4px 10px;
    font-weight: 500;
    border-radius: 12px;
}

.email-replied-badge {
    background-color: #d1fae5;
    color: #065f46;
    border: none;
    font-size: 0.75rem;
    padding: 4px 10px;
    font-weight: 500;
    border-radius: 12px;
}

.timeline-link {
    margin-top: 12px;
    font-size: 0.85rem;
}

.timeline-link a {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
}

.timeline-link a:hover {
    text-decoration: underline;
}

.timeline-link i {
    color: #3b82f6;
}

.card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    font-weight: 600;
}

/* Summary cards styles */
.summary-cards-card {
    border-radius: 10px;
    box-shadow: none;
}
.summary-card {
    border-radius: 12px;
    padding: 18px 12px;
    min-height: 90px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
}
.summary-icon {
    font-size: 18px;
    background: rgba(0, 0, 0, 0.03);
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
}
.summary-count {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
}
.summary-label {
    font-size: 0.875rem;
    color: #64748b;
}
.bg-email {
    background-color: #eef7ff;
}
.bg-notes {
    background-color: #faf3ff;
}
.bg-replies {
    background-color: #eefcf3;
}

@media (max-width: 768px) {
    .lead-header {
        flex-direction: column;
        align-items: flex-start !important;
        gap: 12px;
    }
    .lead-header-right {
        width: 100%;
        justify-content: space-between;
    }
    .owner-info {
        margin-top: 1rem;
        width: 100%;
        justify-content: flex-start;
    }

    .timeline-container {
        max-height: none;
        overflow-y: visible;
    }
}

/* Caution Box */
.email-caution {
    display: flex;
    gap: 12px;
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 20px;
}

.email-caution-strong {
    background: #fee2e2;
    border: 1px solid #fca5a5;
    color: #991b1b;
}

.email-caution-soft {
    background: #fff8e6;
    border: 1px solid #fde68a;
    color: #92400e;
}

.email-caution-icon {
    font-size: 20px;
    margin-top: 2px;
    flex-shrink: 0;
}

.email-caution p {
    margin: 4px 0 0;
    font-size: 13px;
}

/* Leads modals (shared pattern on this page) */
.modal-subtitle {
    font-size: 12px;
    color: var(--slate-500, #64748b);
    margin: 0 0 0.75rem;
    padding: 0.75rem 24px 0;
    text-align: center;
}

.modal-subtitle strong {
    color: var(--slate-900, #0f172a);
    font-weight: 700;
}

.add-note-body {
    padding: 0 24px 24px 24px;
}

.add-note-label {
    font-weight: 600;
    font-size: 14px;
    color: var(--slate-900, #0f172a);
    margin-bottom: 8px;
}

.add-note-textarea,
.add-note-input {
    border: 1px solid var(--slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 14px;
    background-color: #ffffff;
}

.add-note-textarea:focus,
.add-note-input:focus {
    border-color: var(--primary, #2563eb);
    box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.2);
    outline: none;
}

.add-note-textarea::placeholder,
.add-note-input::placeholder {
    color: #9ca3af;
}

/* Ensure regular inputs and textareas don't get dropdown arrow */
.add-note-input[type="text"],
.add-note-input[type="email"],
.add-note-input[type="date"],
.add-note-textarea {
    background-image: none !important;
    padding: 12px 14px !important;
}

/* Select dropdown styling - only for select elements */
::v-deep select.add-note-input,
::v-deep .add-note-input.custom-select,
::v-deep .b-form-select .add-note-input,
::v-deep .b-form-select select {
    border: 1px solid var(--slate-200, #e2e8f0) !important;
    border-radius: 8px !important;
    padding: 12px 40px 12px 14px !important;
    font-size: 14px !important;
    background-color: #ffffff !important;
    height: auto !important;
    min-height: 44px !important;
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E") !important;
    background-repeat: no-repeat !important;
    background-position: right 14px center !important;
    background-size: 12px !important;
    color: #212529 !important;
    cursor: pointer;
}

::v-deep select.add-note-input:focus,
::v-deep .add-note-input.custom-select:focus,
::v-deep .b-form-select select:focus {
    border-color: var(--primary, #2563eb) !important;
    box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.2) !important;
    outline: none !important;
}

::v-deep select.add-note-input:hover,
::v-deep .add-note-input.custom-select:hover,
::v-deep .b-form-select select:hover {
    border-color: #d1d5db !important;
}

.add-note-date-input {
    position: relative;
}

.add-note-date-input::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
}

.add-note-date-input::-webkit-calendar-picker-indicator:hover {
    opacity: 1;
}

.modal-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 0 24px 20px;
}

.modal-actions .btn {
    min-width: 140px;
    border-radius: 10px;
    font-weight: 600;
}

::v-deep .leads-modal .modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

::v-deep .leads-modal .modal-body {
    padding: 0;
}

/* View Full Email Modal Styles */
.view-email-details {
    background: #e0f2fe;
    padding: 20px 24px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 0;
}

.email-details-left,
.email-details-right {
    flex: 1;
}

.email-detail-item {
    margin-bottom: 16px;
}

.email-detail-item:last-child {
    margin-bottom: 0;
}

.email-detail-label {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.email-detail-value {
    font-size: 0.9rem;
    color: #0f172a;
    font-weight: 500;
    line-height: 32px;
}

/* Pill avatar */
.email-avatar {
    display: inline-block;
    min-width: 32px;
    height: 32px;
    padding: 2px 10px;

    border-radius: 999px;
    background-color: #3b82f6;
    color: #ffffff;

    font-weight: 600;
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    text-align: center;
    line-height: 28px;

    vertical-align: middle;
    margin-right: 8px;
}

/* Name text */
.email-owner-name {
    display: inline-block;
    vertical-align: middle;
}

.email-status-replied-badge {
    background-color: #d1fae5;
    color: #065f46;
    border: none;
    font-size: 0.75rem;
    padding: 6px 12px;
    font-weight: 600;
}

.email-status-replied-badge i {
    color: #059669;
}

.email-engagement-alert {
    display: flex;
    gap: 12px;
    background: #d1fae5;
    border: 1px solid #86efac;
    color: #065f46;
    padding: 16px;
    border-radius: 8px;
    margin-top: 20px;
}

.email-engagement-alert i {
    font-size: 20px;
    color: #059669;
    margin-top: 2px;
    flex-shrink: 0;
}

.email-engagement-alert strong {
    display: block;
    font-size: 0.9rem;
    margin-bottom: 4px;
    color: #065f46;
}

.email-engagement-alert p {
    margin: 0;
    font-size: 0.85rem;
    color: #047857;
    line-height: 1.5;
}

.email-editor {
    min-height: 200px;
}

::v-deep .email-editor .ql-container {
    min-height: 200px;
    font-size: 14px;
}

::v-deep .email-editor .ql-editor {
    min-height: 200px;
}

::v-deep .email-editor .ql-toolbar {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border: 1px solid #e5e7eb;
    border-bottom: none;
}

::v-deep .email-editor .ql-container {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    border: 1px solid #e5e7eb;
    border-top: none;
}

/* Communication Notes Styles */
.communication-notes {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(147, 51, 234, 0.2);
}

.note-child-item {
    background: rgba(255, 255, 255, 0.6);
    border-left: 3px solid #9333ea;
    padding: 12px 16px;
    margin-bottom: 12px;
    border-radius: 6px;
}

.note-child-item:last-child {
    margin-bottom: 0;
}

.note-child-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.note-child-label {
    font-weight: 600;
    font-size: 0.8rem;
    color: #9333ea;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.note-child-body {
    font-size: 0.9rem;
    color: #4b5563;
    line-height: 1.5;
    padding-left: 24px;
}
/* Loading overlay styles */
.loading-overlay-container {
    position: relative;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 8px;
}
</style>
